import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import type { TerminalPnlData, TerminalPnlEntry } from './use-finance-terminal-pnl.js';

function fmt(n: number | null | undefined): string {
  if (n == null) return '\u2014';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function TerminalCard({ t, companyAvg }: { t: TerminalPnlEntry; companyAvg: number }) {
  const [expanded, setExpanded] = useState(false);
  const aboveAvg = t.dollar_per_stop >= companyAvg;

  return (
    <div style={{ borderRadius: 12, border: `1px solid ${C.borderSoft}`, overflow: 'hidden' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{ width: '100%', padding: 16, background: C.panel, border: 'none', cursor: 'pointer', color: C.text, textAlign: 'left' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{t.name}</div>
          <span style={{ fontSize: 12, color: C.textMuted }}>{expanded ? '\u25b2' : '\u25bc'}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10 }}>
          {[
            { label: 'Net Revenue', value: fmt(t.net_settlement), color: C.green },
            { label: 'Payroll', value: t.driver_payroll != null ? fmt(t.driver_payroll) : '\u2014', color: C.text },
            { label: 'Net Income', value: t.net_income != null ? fmt(t.net_income) : '\u2014', color: t.net_income != null && t.net_income > 0 ? C.green : C.red },
            { label: '$/Stop', value: `$${t.dollar_per_stop.toFixed(2)}`, color: aboveAvg ? C.green : C.red },
            { label: 'Stops', value: t.total_stops.toLocaleString(), color: C.text },
            { label: 'Drivers', value: String(t.driver_count), color: C.text },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </button>
      {expanded && (
        <div style={{ padding: '0 16px 16px', background: C.panel, display: 'grid', gap: 12 }}>
          {Object.keys(t.revenue_by_type).length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Revenue by type</div>
              {Object.entries(t.revenue_by_type).map(([type, amount]) => (
                <div key={type} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                  <span style={{ color: C.textMuted }}>{type.replace(/_/g, ' ')}</span>
                  <span style={{ color: C.text }}>{fmt(amount)}</span>
                </div>
              ))}
            </div>
          )}
          {t.chargebacks !== 0 && (
            <div style={{ fontSize: 12, color: C.red }}>Chargebacks: {fmt(t.chargebacks)}</div>
          )}
          {t.facilities.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Facilities</div>
              {t.facilities.map(f => (
                <div key={f.facility} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                  <span style={{ color: C.text }}>{f.facility}</span>
                  <span style={{ color: C.textMuted }}>{f.stops} stops \u00b7 {f.drivers} drivers</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface Props { data: TerminalPnlData; onRefresh: () => void; }

export default function FinanceTerminalPnl({ data, onRefresh }: Props) {
  const { terminals, company_avg_dollar_per_stop, period } = data;
  const totalNet = terminals.reduce((s, t) => s + t.net_settlement, 0);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{fmt(totalNet)}</div>
          <div style={{ fontSize: 12, color: C.textMuted }}>
            Total net \u00b7 {terminals.length} terminal{terminals.length !== 1 ? 's' : ''} \u00b7 Co. avg $/stop: ${company_avg_dollar_per_stop.toFixed(2)}
          </div>
        </div>
        <button onClick={onRefresh} style={{ background: 'none', border: `1px solid ${C.borderSoft}`, borderRadius: 6, color: C.textMuted, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Refresh</button>
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        {terminals.sort((a, b) => b.net_settlement - a.net_settlement).map(t => (
          <TerminalCard key={t.isp_number} t={t} companyAvg={company_avg_dollar_per_stop} />
        ))}
      </div>
    </div>
  );
}
